import { useState, type FormEvent } from 'react';
import {
  UserPlus,
  Copy,
  Check,
  X,
  Shield,
  User as UserIcon,
  Trash2,
  Mail,
  AlertCircle,
  ChevronRight,
  ChevronLeft,
  Globe,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  useMembers,
  useInvitations,
  useCreateInvitation,
  useCancelInvitation,
  useRemoveMember,
  useChangeMemberRole,
  useSitePermissions,
  useGrantSitePermission,
  useRevokeSitePermission,
  type Member,
} from '@/hooks/useTeam';
import { useSites } from '@/hooks/useSites';
import { useConfirm } from '@/components/ConfirmDialog';
import { useMe } from '@/hooks/useAuth';
import { formatDate } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { ApiError } from '@/lib/api-client';

type Tab = 'members' | 'invitations' | 'permissions';

export function TeamPage() {
  const [tab, setTab] = useState<Tab>('members');
  const me = useMe();
  const activeOrg = me.data?.organizations.find((o) => o.id === me.data?.active_org_id);
  const isAdmin = activeOrg?.role === 'admin';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Takım</h1>
        <p className="text-muted-foreground text-sm">
          Organizasyon üyeleri, davetler ve site atamaları.
        </p>
      </div>

      <div className="flex border-b">
        {(
          [
            ['members', 'Üyeler'],
            ['invitations', 'Davetler'],
            ['permissions', 'Site Erişimleri'],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={cn(
              'px-4 py-2 text-sm border-b-2 -mb-px transition-colors',
              tab === k
                ? 'border-primary text-foreground font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {!isAdmin && (
        <div className="flex items-start gap-2 text-sm rounded-md border border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/30 px-3 py-2">
          <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <span>Sadece organizasyon yöneticileri takımı düzenleyebilir. Görüntüleme modundasın.</span>
        </div>
      )}

      {tab === 'members' && <MembersTab isAdmin={isAdmin} />}
      {tab === 'invitations' && <InvitationsTab isAdmin={isAdmin} />}
      {tab === 'permissions' && <PermissionsTab isAdmin={isAdmin} />}
    </div>
  );
}

// ---------- Members ----------

function MembersTab({ isAdmin }: { isAdmin: boolean }) {
  const { data, isPending } = useMembers();
  const remove = useRemoveMember();
  const confirm = useConfirm();
  const changeRole = useChangeMemberRole();
  const me = useMe();
  const currentUserId = me.data?.user.id;

  if (isPending) return <div className="text-muted-foreground">Yükleniyor…</div>;

  return (
    <div className="space-y-2">
      {data?.members.map((m) => (
        <Card key={m.user_id}>
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center shrink-0">
              {m.avatar_url ? (
                <img src={m.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                <UserIcon className="w-4 h-4 text-muted-foreground" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium truncate">{m.full_name ?? m.email}</span>
                {m.user_id === currentUserId && (
                  <span className="text-xs text-muted-foreground">(sen)</span>
                )}
                <RoleBadge role={m.role} />
              </div>
              <div className="text-xs text-muted-foreground">
                {m.email} · Katıldı: {formatDate(m.joined_at)}
              </div>
            </div>
            {isAdmin && m.user_id !== currentUserId && (
              <div className="flex items-center gap-1">
                <select
                  value={m.role}
                  onChange={(e) =>
                    changeRole.mutate({ userId: m.user_id, role: e.target.value as 'admin' | 'editor' })
                  }
                  className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                  disabled={changeRole.isPending}
                >
                  <option value="admin">Admin</option>
                  <option value="editor">Editor</option>
                </select>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={async () => {
                    const ok = await confirm({
                      title: 'Üyeyi çıkar?',
                      description: `${m.email} bu organizasyondan çıkarılacak. Site erişimi kaybolur.`,
                      confirmText: 'Çıkar',
                      danger: true,
                    });
                    if (ok) remove.mutate(m.user_id);
                  }}
                  disabled={remove.isPending}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function RoleBadge({ role }: { role: Member['role'] }) {
  if (role === 'admin') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-xs px-2 py-0.5">
        <Shield className="w-3 h-3" /> Admin
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-muted text-muted-foreground text-xs px-2 py-0.5">
      Editor
    </span>
  );
}

// ---------- Invitations ----------

function InvitationsTab({ isAdmin }: { isAdmin: boolean }) {
  const { data, isPending } = useInvitations();
  const create = useCreateInvitation();
  const cancel = useCancelInvitation();
  const confirm = useConfirm();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'editor'>('editor');
  const [error, setError] = useState<string | null>(null);
  const [lastCreated, setLastCreated] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const res = await create.mutateAsync({ email, role });
      setLastCreated(res.invitation.accept_url);
      setEmail('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Davet oluşturulamadı');
    }
  };

  return (
    <div className="space-y-4">
      {isAdmin && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <UserPlus className="w-4 h-4" /> Yeni Davet Oluştur
            </CardTitle>
            <CardDescription>
              Email şu an otomatik gönderilmiyor; link oluştuktan sonra kopyalayıp kişiye gönder.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="flex gap-2 flex-wrap items-end">
              <div className="flex-1 min-w-[200px] space-y-1">
                <Label htmlFor="inv-email">E-posta</Label>
                <Input
                  id="inv-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="editor@mail.com"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="inv-role">Rol</Label>
                <select
                  id="inv-role"
                  value={role}
                  onChange={(e) => setRole(e.target.value as 'admin' | 'editor')}
                  className="flex h-10 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="editor">Editor</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? 'Oluşturuluyor…' : 'Davet Oluştur'}
              </Button>
            </form>
            {error && (
              <div className="mt-3 text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/30 px-3 py-2">
                {error}
              </div>
            )}
            {lastCreated && (
              <div className="mt-3 rounded-md border bg-muted/30 p-3 space-y-2">
                <div className="text-sm font-medium">Davet oluşturuldu — bu linki gönder:</div>
                <CopyLinkBox url={lastCreated} />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {isPending ? (
          <div className="text-muted-foreground">Yükleniyor…</div>
        ) : data?.invitations.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-sm text-muted-foreground">
              <Mail className="w-6 h-6 mx-auto mb-2 opacity-50" />
              Bekleyen davet yok.
            </CardContent>
          </Card>
        ) : (
          data?.invitations.map((inv) => (
            <Card key={inv.id}>
              <CardContent className="p-3 flex items-center gap-3 flex-wrap">
                <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-[150px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{inv.email}</span>
                    <RoleBadge role={inv.role} />
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Oluşturuldu: {formatDate(inv.created_at)} · Son kullanma:{' '}
                    {formatDate(inv.expires_at)}
                  </div>
                </div>
                <CopyLinkBox url={inv.accept_url} compact />
                {isAdmin && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      const ok = await confirm({
                        title: 'Daveti iptal et?',
                        description: 'Bu davet linki geçersiz hale gelir. Davetli kişi bağlantıyı açarsa hata alır.',
                        confirmText: 'İptal et',
                        danger: true,
                      });
                      if (ok) cancel.mutate(inv.id);
                    }}
                    disabled={cancel.isPending}
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}

function CopyLinkBox({ url, compact = false }: { url: string; compact?: boolean }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className={cn('flex gap-2 items-center', !compact && 'w-full')}>
      <code
        className={cn(
          'font-mono bg-background border rounded-md px-2 py-1.5 text-xs truncate',
          compact ? 'max-w-[200px]' : 'flex-1 min-w-0',
        )}
        title={url}
      >
        {url}
      </code>
      <Button size="sm" variant="outline" onClick={onCopy} type="button">
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        {compact ? '' : copied ? 'Kopyalandı' : 'Kopyala'}
      </Button>
    </div>
  );
}

// ---------- Site permissions (dual-listbox) ----------

function PermissionsTab({ isAdmin }: { isAdmin: boolean }) {
  const members = useMembers();
  const sites = useSites();
  const perms = useSitePermissions();
  const grant = useGrantSitePermission();
  const revoke = useRevokeSitePermission();

  const [selectedEditorId, setSelectedEditorId] = useState<string | null>(null);
  // Staged selection in the two boxes — checkboxes pick which rows
  // will move when the arrow fires. Keyed by site id.
  const [pickedAvailable, setPickedAvailable] = useState<Set<string>>(new Set());
  const [pickedGranted, setPickedGranted] = useState<Set<string>>(new Set());

  if (members.isPending || sites.isPending || perms.isPending) {
    return <div className="text-muted-foreground">Yükleniyor…</div>;
  }

  const editors = (members.data?.members ?? []).filter((m) => m.role === 'editor');
  const sitesList = sites.data?.sites ?? [];

  if (editors.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Editor rolünde üye yok. Admin'ler tüm sitelere zaten erişir.
        </CardContent>
      </Card>
    );
  }

  if (sitesList.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Önce bir site ekle.
        </CardContent>
      </Card>
    );
  }

  const grantedFor = (userId: string) =>
    new Set(
      (perms.data?.permissions ?? [])
        .filter((p) => p.user_id === userId)
        .map((p) => p.site_id),
    );

  const selected = editors.find((e) => e.user_id === selectedEditorId) ?? null;
  const grantedIds = selected ? grantedFor(selected.user_id) : new Set<string>();
  const availableSites = sitesList.filter((s) => !grantedIds.has(s.id));
  const grantedSites = sitesList.filter((s) => grantedIds.has(s.id));

  const onSelectEditor = (userId: string) => {
    setSelectedEditorId(userId);
    setPickedAvailable(new Set());
    setPickedGranted(new Set());
  };

  const togglePicked = (set: Set<string>, id: string, setter: (s: Set<string>) => void) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setter(next);
  };

  const onGrant = async () => {
    if (!selected || !isAdmin || pickedAvailable.size === 0) return;
    for (const siteId of pickedAvailable) {
      await grant.mutateAsync({ site_id: siteId, user_id: selected.user_id });
    }
    setPickedAvailable(new Set());
  };

  const onRevoke = async () => {
    if (!selected || !isAdmin || pickedGranted.size === 0) return;
    for (const siteId of pickedGranted) {
      await revoke.mutateAsync({ site_id: siteId, user_id: selected.user_id });
    }
    setPickedGranted(new Set());
  };

  const busy = grant.isPending || revoke.isPending;

  return (
    <div className="grid gap-4 md:grid-cols-[260px_1fr]">
      {/* Editor picker */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Editor seç</CardTitle>
          <CardDescription>Yetki vermek istediğin kullanıcıyı seç.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <ul className="max-h-[480px] overflow-y-auto">
            {editors.map((e) => {
              const isSel = e.user_id === selectedEditorId;
              const count = grantedFor(e.user_id).size;
              return (
                <li key={e.user_id}>
                  <button
                    type="button"
                    onClick={() => onSelectEditor(e.user_id)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2.5 text-left border-b last:border-b-0 transition-colors',
                      isSel
                        ? 'bg-primary/10 text-foreground'
                        : 'hover:bg-muted/50',
                    )}
                  >
                    <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0">
                      {e.avatar_url ? (
                        <img src={e.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                      ) : (
                        <UserIcon className="w-3.5 h-3.5 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{e.full_name ?? e.email}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {count} site yetkisi
                      </div>
                    </div>
                    {isSel && <ChevronRight className="w-4 h-4 text-primary shrink-0" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>

      {/* Dual-listbox */}
      {!selected ? (
        <Card>
          <CardContent className="py-16 text-center text-sm text-muted-foreground">
            Soldan bir editor seç.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              {selected.full_name ?? selected.email} — site yetkileri
            </CardTitle>
            <CardDescription>
              Soldan site seçip → ile sağa taşı: seçili siteler üzerinde yazı oluşturma, dispatch ve silme yetkisi verilir.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-stretch">
              <SiteListbox
                title="Yetkisiz Siteler"
                sites={availableSites}
                picked={pickedAvailable}
                onToggle={(id) => togglePicked(pickedAvailable, id, setPickedAvailable)}
                emptyText="Bu kullanıcı tüm sitelerde yetkili."
                disabled={!isAdmin || busy}
              />
              <div className="flex flex-col justify-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onGrant}
                  disabled={!isAdmin || busy || pickedAvailable.size === 0}
                  title={`${pickedAvailable.size} site taşı`}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onRevoke}
                  disabled={!isAdmin || busy || pickedGranted.size === 0}
                  title={`${pickedGranted.size} site geri al`}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
              </div>
              <SiteListbox
                title="Yetkili Siteler"
                sites={grantedSites}
                picked={pickedGranted}
                onToggle={(id) => togglePicked(pickedGranted, id, setPickedGranted)}
                emptyText="Henüz site yetkisi yok."
                disabled={!isAdmin || busy}
              />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SiteListbox({
  title,
  sites,
  picked,
  onToggle,
  emptyText,
  disabled,
}: {
  title: string;
  sites: Array<{ id: string; name: string; site_url: string }>;
  picked: Set<string>;
  onToggle: (id: string) => void;
  emptyText: string;
  disabled: boolean;
}) {
  return (
    <div className="rounded-md border bg-muted/20 flex flex-col min-h-[300px]">
      <div className="px-3 py-2 border-b bg-background/50 flex items-center justify-between">
        <span className="text-xs font-medium">{title}</span>
        <span className="text-xs text-muted-foreground font-mono">{sites.length}</span>
      </div>
      {sites.length === 0 ? (
        <div className="flex-1 flex items-center justify-center py-6 px-3 text-xs text-muted-foreground text-center">
          {emptyText}
        </div>
      ) : (
        <ul className="flex-1 overflow-y-auto max-h-[380px]">
          {sites.map((s) => {
            const isPicked = picked.has(s.id);
            return (
              <li key={s.id}>
                <label
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 border-b last:border-b-0 cursor-pointer transition-colors',
                    isPicked ? 'bg-primary/10' : 'hover:bg-muted/40',
                    disabled && 'opacity-60 cursor-not-allowed',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={isPicked}
                    onChange={() => onToggle(s.id)}
                    disabled={disabled}
                    className="h-4 w-4 accent-primary shrink-0"
                  />
                  <Globe className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">{s.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{s.site_url}</div>
                  </div>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
